import React, { useEffect, useRef, useState } from "react";

import styles from "./FlipDigit.module.scss";

interface Props {
    value: string;
}

export const FlipDigit = ({ value }: Props) => {
    const [current, setCurrent] = useState(value);
    const [previous, setPrevious] = useState(value);
    const [flipping, setFlipping] = useState(false);
    const prevRef = useRef(value);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    useEffect(() => {
        if (value === prevRef.current) return;

        setPrevious(prevRef.current);
        setCurrent(value);
        setFlipping(true);
        prevRef.current = value;

        clearTimeout(timeoutRef.current);
        // Must exceed $flip-duration (350ms) + $flip-delay (400ms) = 750ms.
        timeoutRef.current = setTimeout(() => {
            setFlipping(false);
        }, 900);

        return () => clearTimeout(timeoutRef.current);
    }, [value]);

    return (
        <span className={styles.digit} aria-label={value}>
            {/* Static bottom half — shows old value until the bottom flap covers it */}
            <span className={styles.half}>
                <span className={styles.bottomStatic}>
                    {flipping ? previous : current}
                </span>
            </span>

            {/* Static top half — shows the CURRENT (new) value once flip completes */}
            <span className={styles.half}>
                <span className={styles.topStatic}>{current}</span>
            </span>

            {/* Animated top half — flips down to reveal next value */}
            {flipping && (
                <span className={styles.half}>
                    <span className={styles.topFlip}>{previous}</span>
                </span>
            )}

            {/* Animated bottom half — flips up into place */}
            {flipping && (
                <span className={styles.half}>
                    <span className={styles.bottomFlip}>{current}</span>
                </span>
            )}

            {/* Shadow layer — persistent, shows old value during flip, new value when idle.
                Hides at $flip-delay when shadowOverlay takes over, reappears when flipping ends. */}
            <span className={styles.half}>
                <span
                    className={`${styles.shadow} ${flipping ? styles.shadowFlipping : ""}`}
                >
                    {flipping ? previous : current}
                </span>
            </span>

            {/* Shadow overlay — shows the new value, hidden until the bottom
                flap starts, then appears (hard cut) to replace the old shadow */}
            {flipping && (
                <span className={styles.half}>
                    <span className={styles.shadowOverlay}>{current}</span>
                </span>
            )}

            {/* Divider line across the middle */}
            <span className={styles.divider} />
        </span>
    );
};
